// Help content for each checklist task
// Provides detailed guidance on what the task means and how to accomplish it

export interface TaskHelpContent {
  title: string;
  description: string;
  steps?: string[];
  tips?: string[];
  resources?: { label: string; url?: string }[];
}

export const TASK_HELP: Record<string, TaskHelpContent> = {
  // ============================================
  // IMMEDIATE TASKS
  // ============================================

  "immediate-confirm-death-certificate": {
    title: "Death Certificate",
    description:
      "A death certificate is an official document that records the date, location, and cause of death. It's required for nearly all legal and financial matters following a death.",
    steps: [
      "If the death occurred in a hospital or hospice, staff will typically handle the initial paperwork",
      "For deaths at home, call 911 or the person's doctor to have death pronounced",
      "The funeral home will usually file for the official death certificate on your behalf",
      "Request 10-15 certified copies - you'll need them for banks, insurance, and legal matters",
    ],
    tips: [
      "Certified copies cost money, but photocopies aren't accepted for most official purposes",
      "Some states allow online ordering of additional copies later",
      "Keep the original in a safe place and use certified copies for transactions",
    ],
  },

  "immediate-designate-primary-contact": {
    title: "Designating a Primary Contact",
    description:
      "Having one person serve as the main point of contact helps coordinate communication and prevents confusion during a difficult time.",
    steps: [
      "Choose someone who is organized and comfortable making phone calls",
      "This person will communicate with the funeral home, vendors, and extended family",
      "Create a shared document or group chat to keep everyone informed",
      "Consider having a backup person for when the primary contact needs a break",
    ],
    tips: [
      "The primary contact doesn't have to be the person making all the decisions",
      "Delegating this role can help distribute the emotional burden",
      "Keep a list of important phone numbers easily accessible",
    ],
  },

  "immediate-notify-close-family": {
    title: "Notifying Family and Friends",
    description:
      "Letting loved ones know about the death is one of the first and most important steps. It's okay to ask for help with this.",
    steps: [
      "Start with immediate family members first",
      "Consider making a list of people who need to be notified personally vs. those who can learn through others",
      "For close friends and family, phone calls are usually most appropriate",
      "You can ask trusted friends or family members to help make calls",
    ],
    tips: [
      "It's okay to keep the conversation brief - you don't need to share all the details",
      "A simple script can help: 'I'm calling to let you know that [name] passed away [timeframe].'",
      "For acquaintances, an email or social media post may be appropriate after close ones are notified",
      "Don't feel pressured to answer questions you're not ready to discuss",
    ],
  },

  "immediate-notify-work-or-colleagues": {
    title: "Notifying Workplace",
    description:
      "Informing the person's employer allows them to handle workplace matters and may trigger benefits like life insurance or final paychecks.",
    steps: [
      "Contact their direct supervisor or HR department",
      "Ask about any life insurance, retirement benefits, or final pay",
      "Inquire about returning company property (laptop, ID badge, etc.)",
      "Ask if there are any outstanding expense reimbursements",
    ],
    tips: [
      "Many employers offer bereavement support or EAP services to family members",
      "Keep a record of who you spoke with and when",
      "Ask about COBRA health insurance continuation if family members were on the plan",
    ],
  },

  "immediate-contact-preplanned-funeral-home": {
    title: "Contacting Pre-Arranged Funeral Home",
    description:
      "If pre-arrangements were made, the funeral home already has information about wishes and may have prepaid services.",
    steps: [
      "Locate the pre-arrangement documents if possible",
      "Call the funeral home - they're available 24/7",
      "Provide the contract number or the person's name",
      "They will guide you through the next steps and arrange transportation",
    ],
    tips: [
      "Even with pre-arrangements, you may need to make some decisions",
      "Review what was prepaid vs. what additional services might cost",
      "Pre-arrangements can sometimes be transferred to a different funeral home if needed",
    ],
  },

  "immediate-contact-funeral-home": {
    title: "Choosing a Funeral Home",
    description:
      "A funeral home will help coordinate most aspects of the arrangements. Take time to find one that feels right for your family.",
    steps: [
      "Ask friends, family, or hospice staff for recommendations",
      "Call 2-3 funeral homes to compare services and prices",
      "By law, they must provide a General Price List over the phone",
      "Consider location, services offered, and how comfortable you feel with the staff",
    ],
    tips: [
      "You are not required to use the funeral home closest to where the death occurred",
      "Prices can vary significantly between funeral homes",
      "Many funeral homes offer package deals, but you can choose services à la carte",
      "Trust your instincts about the staff's compassion and professionalism",
    ],
  },

  "immediate-locate-will": {
    title: "Locating the Will",
    description:
      "A will outlines the person's wishes for their estate and may include instructions for funeral arrangements.",
    steps: [
      "Check common locations: home safe, filing cabinet, with their attorney",
      "Look for a safe deposit box key (you may need a death certificate to access it)",
      "Contact their attorney if you know who it is",
      "Check if the will was filed with the local probate court",
    ],
    tips: [
      "Read the will carefully for any funeral or burial wishes",
      "The executor named in the will has authority over the estate",
      "Don't be surprised if the will is outdated - focus on clear intentions",
      "If there's no will, state law determines how assets are distributed",
    ],
  },

  "immediate-search-for-documents": {
    title: "Searching for Important Documents",
    description:
      "Even without a formal will, there may be other documents that indicate the person's wishes or contain important information.",
    steps: [
      "Search their home office, bedroom, and any filing areas",
      "Look for a safe deposit box key",
      "Check email for any digital documents or accounts",
      "Contact their bank, attorney, or financial advisor",
    ],
    tips: [
      "Look for: birth certificate, Social Security card, insurance policies, bank statements, property deeds",
      "A letter of instruction (even informal) may outline wishes",
      "Check if they used an online estate planning service",
      "Some people keep important documents with a trusted family member",
    ],
  },

  "immediate-locate-key-documents": {
    title: "Gathering Important Documents",
    description:
      "You'll need various documents for insurance claims, bank accounts, and legal matters. Gathering them early saves time later.",
    steps: [
      "Collect: birth certificate, Social Security card, marriage certificate (if applicable)",
      "Find insurance policies (life, health, auto, home)",
      "Locate recent tax returns and bank/investment statements",
      "Gather property deeds, vehicle titles, and loan documents",
    ],
    tips: [
      "Make copies of everything before submitting originals anywhere",
      "Create a folder or binder to keep documents organized",
      "You may not need everything immediately - prioritize based on urgency",
      "Check for digital accounts and password managers",
    ],
  },

  "immediate-secure-pets-dependents": {
    title: "Caring for Pets and Dependents",
    description:
      "Arranging immediate care for those who depended on your loved one is a priority that needs quick attention.",
    steps: [
      "Identify all pets and their immediate needs (food, medication, walks)",
      "Arrange for someone to care for them temporarily",
      "Check if there are written wishes for pet care",
      "For human dependents, ensure they have appropriate supervision and support",
    ],
    tips: [
      "Pets can sense changes and may need extra comfort",
      "Keep routines as normal as possible for children",
      "Contact schools or daycares to inform them of the situation",
      "Don't make permanent decisions about pet rehoming right away",
    ],
  },

  "immediate-secure-home": {
    title: "Securing the Home",
    description:
      "Protecting the person's home and belongings prevents problems and gives you time to make thoughtful decisions.",
    steps: [
      "Collect mail and newspapers to avoid signaling an empty home",
      "Ensure doors and windows are locked",
      "Set lights on timers or ask a neighbor to check periodically",
      "Secure valuables like jewelry, cash, and important documents",
    ],
    tips: [
      "Don't rush to clean out or dispose of belongings",
      "Take photos of valuable items for insurance purposes",
      "If the person lived alone, inform a trusted neighbor",
      "Check that utilities will continue and bills are being paid",
    ],
  },

  "immediate-notify-place-of-worship": {
    title: "Notifying Faith Community",
    description:
      "Religious communities often provide meaningful support and may help with services, meals, and emotional care.",
    steps: [
      "Call the main office or the clergy member directly",
      "Inform them of the death and any immediate needs",
      "Ask about holding services at the place of worship",
      "Inquire about support services like meals, visits, or prayer groups",
    ],
    tips: [
      "Many faith communities have established processes for supporting grieving families",
      "Clergy can help guide you through religious traditions and customs",
      "Don't hesitate to accept offers of help - it's meaningful for the community too",
      "Ask about any religious requirements for timing or preparation of the body",
    ],
  },

  "immediate-discuss-religious-customs": {
    title: "Religious Customs and Requirements",
    description:
      "Different faith traditions have specific customs around death, burial timing, and mourning. Understanding these helps honor your loved one appropriately.",
    steps: [
      "Meet or speak with clergy as soon as possible",
      "Ask about any time-sensitive requirements (some traditions require quick burial)",
      "Discuss preparation of the body according to religious customs",
      "Understand expectations for the funeral service and mourning period",
    ],
    tips: [
      "It's okay to adapt traditions to your family's comfort level",
      "Ask questions - clergy are accustomed to guiding families through this",
      "Some customs may affect your choice of funeral home (kosher preparation, etc.)",
      "Religious communities often have volunteers to help with customs",
    ],
  },

  "immediate-contact-chevra-kadisha": {
    title: "Jewish Burial Society (Chevra Kadisha)",
    description:
      "The Chevra Kadisha performs the sacred ritual of preparing the body (tahara) according to Jewish tradition.",
    steps: [
      "Contact your synagogue or the local Jewish funeral home",
      "The Chevra Kadisha will coordinate with the funeral home",
      "Discuss whether you want traditional preparation (tahara) and burial shrouds",
      "Jewish tradition typically calls for burial within 24 hours, though exceptions are made",
    ],
    tips: [
      "Tahara is performed by trained volunteers as an act of loving-kindness",
      "The family does not need to be present for tahara",
      "Discuss any questions about custom with your rabbi",
      "Shomer (guarding the body) may be arranged until burial",
    ],
  },

  "immediate-contact-islamic-center": {
    title: "Islamic Burial Guidance",
    description:
      "Islamic tradition has specific requirements for preparing the body (ghusl) and burial that should be followed.",
    steps: [
      "Contact your local mosque or Islamic center immediately",
      "They will help arrange ghusl (ritual washing) by trained community members",
      "Burial should occur as soon as possible, ideally within 24 hours",
      "The body is wrapped in white cloth (kafan) and buried facing Mecca",
    ],
    tips: [
      "Embalming and cremation are generally not permitted in Islamic tradition",
      "A simple wooden casket or no casket may be used depending on local regulations",
      "The mosque can help find an appropriate cemetery",
      "Funeral prayers (Salat al-Janazah) are typically held at the mosque",
    ],
  },

  "immediate-contact-hindu-priest": {
    title: "Hindu Last Rites",
    description: "Hindu traditions include specific rituals (Antyesti) that help the soul transition peacefully.",
    steps: [
      "Contact a Hindu priest (pandit) in your community",
      "Discuss the appropriate rituals for your family's tradition",
      "Cremation is traditional in Hinduism, usually within 24 hours",
      "The priest will guide the family through prayers and rituals",
    ],
    tips: [
      "Customs vary by region and family tradition in Hinduism",
      "The eldest son traditionally performs certain rituals, but alternatives exist",
      "Discuss any specific wishes with the priest",
      "Post-cremation rituals continue for 13 days (varies by tradition)",
    ],
  },

  "immediate-locate-military-records": {
    title: "Military Discharge Papers (DD-214)",
    description:
      "The DD-214 is essential for claiming military benefits including burial honors, headstone, and survivor benefits.",
    steps: [
      "Check the veteran's personal files and safe deposit box",
      "Contact the National Personnel Records Center if you can't find it",
      "Online request: eVetRecs at archives.gov",
      "You may need to provide proof of death and your relationship",
    ],
    tips: [
      "Processing requests can take several weeks, so start early",
      "Many funeral homes can help expedite records requests",
      "A copy of the DD-214 is sufficient for most purposes",
      "Veterans service organizations (VFW, American Legion) can help navigate this process",
    ],
  },

  "preplanning-discuss-wishes": {
    title: "Discussing End-of-Life Wishes",
    description:
      "Having open conversations about funeral preferences helps ensure wishes are honored and reduces stress for family members.",
    steps: [
      "Choose a comfortable, private time for the conversation",
      "Ask open-ended questions about preferences for services, burial vs. cremation, etc.",
      "Discuss any religious or cultural traditions that are important",
      "Document the wishes in writing and share with family members",
    ],
    tips: [
      "These conversations can happen over multiple sessions",
      "It's okay if some things are undecided",
      "Consider discussing healthcare wishes and advance directives too",
      "Reassure them that knowing their wishes is a gift to the family",
    ],
  },

  "preplanning-research-options": {
    title: "Researching Pre-Planning Options",
    description:
      "Pre-planning allows someone to make their own choices and can ease the financial and emotional burden on family.",
    steps: [
      "Research funeral homes in the preferred area",
      "Request General Price Lists from several providers",
      "Compare pre-payment options (insurance-funded vs. trust-funded)",
      "Understand what is and isn't included in pre-planned packages",
    ],
    tips: [
      "Pre-planning doesn't require pre-payment",
      "Pre-paid plans may be transferable if you move",
      "Ask about price guarantees vs. prices locked at time of payment",
      "Consider consulting with a financial advisor about pre-payment",
    ],
  },

  "immediate-review-organ-donation": {
    title: "Organ Donation Wishes",
    description:
      "If organ donation was desired, time is critical. Even if the person was registered, family consent may be needed.",
    steps: [
      "Check if they were registered as an organ donor (driver's license, donor registry)",
      "If in a hospital, the organ procurement organization will be contacted automatically",
      "For other locations, contact your local OPO if donation was desired",
      "Understand that organ donation doesn't delay funeral arrangements",
    ],
    tips: [
      "Organ donation is possible even with some medical conditions",
      "Donation doesn't affect the ability to have an open casket",
      "Tissue donation (corneas, skin, bone) is possible even after death at home",
      "Donated organs typically help multiple recipients",
    ],
  },

  "immediate-contact-body-donation-program": {
    title: "Body Donation to Science",
    description:
      "If the person arranged to donate their body to medical education or research, the program must be contacted promptly.",
    steps: [
      "Locate the paperwork for the body donation program",
      "Contact the program's 24-hour number immediately",
      "They will arrange transportation of the body",
      "Understand the timeline for when remains may be returned",
    ],
    tips: [
      "Most programs cover transportation and cremation costs",
      "The body is typically returned to the family after 1-2 years",
      "A memorial service can still be held without the body present",
      "If the program cannot accept the body, have a backup plan ready",
    ],
  },

  // ============================================
  // SHORT-TERM TASKS
  // ============================================

  "short-plan-service-details": {
    title: "Planning Service Details",
    description: "The service is a chance to honor your loved one's life. There's no single right way to do it.",
    steps: [
      "Decide on a date that allows important people to attend",
      "Choose the format: religious service, celebration of life, graveside only, etc.",
      "Select a venue (funeral home, place of worship, outdoor location)",
      "Plan the order of service: readings, music, speakers, rituals",
    ],
    tips: [
      "It's okay for the service to reflect the person's personality, even if unconventional",
      "Consider accessibility needs for elderly or disabled attendees",
      "Build in time for attendees to share memories if desired",
      "A short, meaningful service is better than a long, exhausting one",
    ],
  },

  "short-meet-with-funeral-director": {
    title: "Meeting with the Funeral Director",
    description:
      "The funeral director will guide you through options and paperwork. Come prepared but don't feel rushed.",
    steps: [
      "Bring important documents (ID, insurance policies, military records if applicable)",
      "Review the General Price List and ask about each charge",
      "Discuss the timeline and what happens when",
      "Ask about payment plans if cost is a concern",
    ],
    tips: [
      "You don't have to decide everything in one meeting",
      "Ask for an itemized estimate in writing",
      "You can provide your own casket or urn if desired",
      "Don't be afraid to say 'I need to think about it'",
    ],
  },

  "short-select-burial-plot": {
    title: "Selecting a Burial Plot",
    description:
      "Choosing where your loved one will rest is a meaningful decision. Consider location, cost, and any family plots.",
    steps: [
      "Check if there's a family plot or pre-purchased space",
      "Visit cemeteries to see the grounds and available sections",
      "Ask about perpetual care (ongoing maintenance)",
      "Understand all costs: plot, opening/closing, vault, marker installation",
    ],
    tips: [
      "Some cemeteries are affiliated with specific religions",
      "Consider how accessible the location is for future visits",
      "Ask about policies on decorations and flowers",
      "Veterans may be eligible for burial in national cemeteries",
    ],
  },

  "short-choose-casket": {
    title: "Choosing a Casket",
    description: "Caskets range widely in price and material. Choose what feels right for your family and budget.",
    steps: [
      "Review options at the funeral home or online retailers",
      "Understand the materials: wood, metal, eco-friendly options",
      "Consider if there will be a viewing (affects choice)",
      "Ask about rental caskets for viewing if cremation will follow",
    ],
    tips: [
      "Expensive doesn't mean more meaningful",
      "You can legally purchase a casket from any seller",
      "The funeral home cannot charge a fee for using an outside casket",
      "Simple wood or cloth-covered caskets can be dignified and affordable",
    ],
  },

  "short-select-urn": {
    title: "Selecting an Urn",
    description: "Urns come in many styles and sizes. Your choice depends on plans for the cremains.",
    steps: [
      "Decide what will happen to the cremains (keep, scatter, bury, divide)",
      "Choose an urn size based on your plans (standard, keepsake, biodegradable)",
      "Consider materials and style that reflect the person",
      "You can wait to choose an urn - cremains are initially returned in a temporary container",
    ],
    tips: [
      "You don't need an urn if you're scattering ashes",
      "Keepsake urns allow multiple family members to have a portion",
      "Biodegradable urns are available for water scattering or burial",
      "Custom urns can be made from meaningful materials or in unique shapes",
    ],
  },

  "short-green-burial-requirements": {
    title: "Green Burial Requirements",
    description: "Green burials minimize environmental impact using biodegradable materials and no embalming.",
    steps: [
      "Find a cemetery that allows green burials",
      "Understand what containers are approved (shroud, plain wood, wicker)",
      "Confirm embalming is not required (or only eco-friendly embalming)",
      "Ask about grave depth and markers allowed",
    ],
    tips: [
      "Not all cemeteries allow green burials - research in advance",
      "Conservation cemeteries protect the land in perpetuity",
      "Some traditional cemeteries have green burial sections",
      "A body can be refrigerated instead of embalmed for short-term preservation",
    ],
  },

  "short-arrange-visitation": {
    title: "Arranging Visitation",
    description:
      "A visitation or viewing gives people the chance to pay respects and support the family before the funeral.",
    steps: [
      "Decide on timing: evening before, morning of, or combined with service",
      "Choose whether to have the casket open or closed",
      "Plan the hours and inform people of the schedule",
      "Prepare personal items or photos for display",
    ],
    tips: [
      "Visitations can be just for family or open to everyone",
      "An open casket isn't required or expected",
      "Consider having a private family viewing separate from public hours",
      "It's okay to limit the hours to prevent exhaustion",
    ],
  },

  "short-choose-venue-church": {
    title: "Coordinating with Place of Worship",
    description: "Holding the service at a place of worship provides a meaningful setting and built-in support.",
    steps: [
      "Confirm availability with the church/synagogue/mosque office",
      "Discuss who will officiate and any guest speakers",
      "Review any policies on music, flowers, or photography",
      "Coordinate timing with the cemetery or crematory",
    ],
    tips: [
      "There may be fees for using the space and staff",
      "Ask about sound system, programs, and other logistics",
      "The venue may have people who can help with setup and ushering",
      "Confirm any restrictions on what can be said or done during the service",
    ],
  },

  "short-choose-venue-other": {
    title: "Choosing a Service Venue",
    description: "Services can be held in many locations beyond traditional funeral homes and churches.",
    steps: [
      "Consider meaningful locations: parks, beaches, community halls, family home",
      "Check permit requirements for public spaces",
      "Ensure adequate seating, parking, and weather protection",
      "Plan for sound amplification if needed",
    ],
    tips: [
      "Funeral homes often have chapels you can use without other services",
      "Restaurants or hotels may host memorial receptions",
      "Consider practical needs like restrooms and accessibility",
      "Have a backup plan if the venue is outdoors",
    ],
  },

  "short-coordinate-officiant-clergy": {
    title: "Coordinating with Clergy",
    description:
      "Clergy will lead the religious elements of the service and often provide pastoral support to the family.",
    steps: [
      "Meet with the officiant to discuss the service format",
      "Share information about the person for the eulogy or sermon",
      "Discuss any specific prayers, readings, or rituals to include",
      "Confirm their availability and any honorarium",
    ],
    tips: [
      "Family members can still participate in the service",
      "Provide stories and details to help personalize their remarks",
      "Ask about meeting with them for pastoral support",
      "Clergy can help guide you on religious customs you're unsure about",
    ],
  },

  "short-find-officiant": {
    title: "Finding an Officiant",
    description: "A non-religious service can be led by a celebrant, family member, or friend who can speak publicly.",
    steps: [
      "Decide if you want a professional celebrant or someone you know",
      "Search for certified celebrants in your area",
      "Interview potential officiants to find the right fit",
      "Discuss the tone and content you want for the service",
    ],
    tips: [
      "Funeral celebrants specialize in creating personalized ceremonies",
      "A confident friend or family member can do a wonderful job",
      "Prepare a detailed outline so the officiant knows what to cover",
      "Rehearse if possible, especially if multiple speakers are involved",
    ],
  },

  "short-request-military-honors": {
    title: "Requesting Military Honors",
    description: "All veterans are entitled to military funeral honors, including flag folding and presentation.",
    steps: [
      "Inform the funeral home of the veteran's service",
      "Provide the DD-214 or other proof of service",
      "The funeral home typically coordinates with the military",
      "Discuss what honors you'd like: flag folding, rifle salute, bugler",
    ],
    tips: [
      "Basic honors (two-person team, flag folding) are guaranteed by law",
      "Full military honors depend on availability and veteran's rank/service",
      "Honors can be provided at any funeral venue",
      "Veterans service organizations may provide additional support",
    ],
  },

  "short-arrange-flag-presentation": {
    title: "Flag Presentation Ceremony",
    description:
      "The American flag is folded and presented to the next of kin as a symbol of gratitude for the veteran's service.",
    steps: [
      "Confirm the flag presentation is part of the military honors",
      "Decide who will receive the flag (usually spouse or eldest child)",
      "The military representative will present with official wording",
      "Plan where in the service this will occur",
    ],
    tips: [
      "Additional flags can be requested for other family members",
      "The flag can be displayed in a memorial case",
      "Some families choose to have the flag draped over the casket",
      "The presentation is a solemn moment - tissues should be nearby",
    ],
  },

  "short-write-obituary": {
    title: "Writing an Obituary",
    description:
      "An obituary announces the death and celebrates the person's life. It can be published in newspapers and online.",
    steps: [
      "Gather basic information: full name, dates, family members, career",
      "Include meaningful details: hobbies, accomplishments, personality",
      "Note service information: date, time, location, any requests",
      "Decide where to publish: local paper, online, funeral home website",
    ],
    tips: [
      "Keep it personal - share what made them special",
      "Obituaries can include humor if it fits the person",
      "Ask family members to contribute memories or stories",
      "Newspaper obituaries charge by length; online is often free",
    ],
  },

  "short-gather-photos": {
    title: "Gathering Photos",
    description:
      "Photos help tell the story of a life and give attendees something to connect with during the service.",
    steps: [
      "Collect photos from all stages of life",
      "Ask family members to share their favorites",
      "Choose a mix of formal and candid shots",
      "Consider creating a slideshow or display boards",
    ],
    tips: [
      "Scan old photos for digital use and preservation",
      "Include photos showing hobbies, relationships, and personality",
      "A childhood-to-present sequence tells a life story",
      "Video clips can be powerful additions to a slideshow",
    ],
  },

  "short-select-readings-music": {
    title: "Selecting Readings and Music",
    description: "Music and readings set the tone and provide moments of reflection during the service.",
    steps: [
      "Choose music the person loved or that has special meaning",
      "Select readings: religious texts, poems, song lyrics, personal letters",
      "Decide who will read or if recordings will be used",
      "Balance somber and uplifting moments",
    ],
    tips: [
      "Live music adds a personal touch but isn't required",
      "Non-traditional music is okay - this is about honoring the person",
      "Readers should practice beforehand",
      "Have backup readers in case someone becomes too emotional",
    ],
  },

  "short-coordinate-pallbearers": {
    title: "Selecting Pallbearers",
    description: "Pallbearers carry or escort the casket, a meaningful honor often given to close friends and family.",
    steps: [
      "Traditionally, six pallbearers carry the casket",
      "Consider physical ability - it can be heavy",
      "Ask people personally if they're willing to serve",
      "Have honorary pallbearers if you want to include more people",
    ],
    tips: [
      "Pallbearers can be any gender",
      "Funeral home staff can assist or substitute if needed",
      "Some caskets have wheels and require minimal lifting",
      "Honorary pallbearers walk alongside without carrying",
    ],
  },

  "short-plan-livestream": {
    title: "Planning Virtual Attendance",
    description: "Livestreaming allows people who can't travel to participate in the service from anywhere.",
    steps: [
      "Ask the funeral home if they offer streaming services",
      "If DIY, test equipment and internet connection in advance",
      "Choose a platform: Zoom, Facebook Live, YouTube, etc.",
      "Share the link and access instructions with remote attendees",
    ],
    tips: [
      "Assign someone to monitor the stream and chat",
      "Position the camera to show the officiant and key moments",
      "Record the stream for those who can't watch live",
      "Consider audio quality as much as video",
    ],
  },

  "short-order-flowers": {
    title: "Ordering Flowers",
    description:
      "Flowers add beauty to services and express sympathy. You can also request charitable donations instead.",
    steps: [
      "Order flowers for the casket or urn arrangement",
      "Decide if you'll have additional displays",
      "Let others know if they can send flowers or if you prefer donations",
      "Plan what happens to flowers after the service",
    ],
    tips: [
      "Many cultures have specific flower traditions",
      "Living plants last longer than cut flowers",
      "Specify a charity in the obituary for 'in lieu of flowers'",
      "Some families donate flowers to nursing homes after the service",
    ],
  },

  "short-order-printed-materials": {
    title: "Ordering Printed Materials",
    description: "Programs, prayer cards, and memorial items give attendees something to keep.",
    steps: [
      "Create or select a design for the program",
      "Include: order of service, obituary highlights, photos, poems",
      "Estimate attendance and order enough copies",
      "Consider additional items: prayer cards, bookmarks, memorial folders",
    ],
    tips: [
      "Many funeral homes offer printing services",
      "Online services can create professional programs quickly",
      "Keep the design simple and readable",
      "Save the digital file for reprints if needed",
    ],
  },

  "short-gather-clothing": {
    title: "Gathering Clothing for Burial",
    description: "Choosing what the person will wear is a personal decision. Pick something meaningful.",
    steps: [
      "Choose an outfit they loved or one with special meaning",
      "Consider comfort and appearance (loose-fitting often works better)",
      "Include any personal items: glasses, jewelry, photos",
      "Bring items to the funeral home before the viewing",
    ],
    tips: [
      "The outfit doesn't have to be formal - it should reflect the person",
      "Some families choose meaningful items: sports jersey, hobby attire, etc.",
      "Consider removing jewelry after viewing if desired",
      "Military uniforms can often be obtained for veterans",
    ],
  },

  "short-plan-reception-venue": {
    title: "Booking a Reception Venue",
    description: "A reception gives people time to connect, share memories, and support each other after the service.",
    steps: [
      "Estimate how many people will attend",
      "Consider locations: church hall, restaurant, family home, community center",
      "Check availability and capacity",
      "Confirm any restrictions on food, alcohol, or time",
    ],
    tips: [
      "Closer to the service venue is easier for attendees",
      "Some venues provide catering; others allow outside food",
      "Have clear directions or maps available",
      "Plan for children if they'll be attending",
    ],
  },

  "short-arrange-catering": {
    title: "Arranging Food for the Reception",
    description: "Sharing a meal is a time-honored way to bring people together after a loss.",
    steps: [
      "Decide on the style: sit-down meal, buffet, light refreshments",
      "Consider dietary restrictions in the group",
      "Get quotes from caterers or restaurants",
      "Ask if friends and family want to bring food",
    ],
    tips: [
      "Simple is fine - sandwiches and snacks work well",
      "Many communities bring food without being asked",
      "Label dishes with allergen information",
      "Plan for leftovers - send food home with family",
    ],
  },

  "short-reception-logistics": {
    title: "Planning Reception Logistics",
    description: "Good planning ensures the reception runs smoothly so people can focus on connecting.",
    steps: [
      "Arrange seating appropriate for the space and attendance",
      "Plan parking or transportation from the service",
      "Coordinate timing - when will people arrive?",
      "Assign helpers for setup, greeting, and cleanup",
    ],
    tips: [
      "Have a sign-in book for guests if desired",
      "Create a photo or memory display area",
      "Provide tissue boxes throughout the space",
      "Plan a quiet space for those who need a break",
    ],
  },

  "short-arrange-transportation": {
    title: "Arranging Transportation",
    description: "Coordinating transportation ensures family members can focus on the service rather than logistics.",
    steps: [
      "Arrange a family car or limousine from funeral home",
      "Plan transportation from service to cemetery to reception",
      "Consider elderly or disabled family members' needs",
      "Coordinate carpools for others if helpful",
    ],
    tips: [
      "Funeral home limousines are traditional but not required",
      "Family members shouldn't have to navigate alone",
      "Have someone else drive immediate family",
      "Check if any out-of-town guests need airport transportation",
    ],
  },

  "preplanning-compare-costs": {
    title: "Comparing Funeral Costs",
    description: "Funeral costs vary significantly. Understanding the options helps make informed decisions.",
    steps: [
      "Request General Price Lists from several funeral homes",
      "Compare like services: basic services fee, embalming, facilities, vehicles",
      "Ask about package deals vs. à la carte pricing",
      "Inquire about payment plans and pre-payment options",
    ],
    tips: [
      "Funeral homes must provide prices by law",
      "The most expensive option isn't necessarily the best",
      "Consider what's truly important vs. what's traditional",
      "Cemetery costs are separate from funeral home costs",
    ],
  },

  "preplanning-document-wishes": {
    title: "Documenting Funeral Preferences",
    description: "Written wishes remove guesswork and potential family disagreements during an emotional time.",
    steps: [
      "Write out preferences for service type, burial vs. cremation",
      "Note any specific requests: songs, readings, attendees",
      "Include practical information: account numbers, contacts",
      "Store copies with family, attorney, and in a safe place",
    ],
    tips: [
      "Be as specific or general as you prefer",
      "Update the document as wishes change",
      "Include digital account information and passwords",
      "Consider an ethical will to share values and memories",
    ],
  },

  "preplanning-consider-prepayment": {
    title: "Considering Pre-Payment",
    description: "Pre-paying locks in prices and ensures funds are available, but understand the terms carefully.",
    steps: [
      "Understand the difference between insurance-funded and trust-funded plans",
      "Ask if prices are guaranteed or if family pays the difference",
      "Check if plans are transferable if you move",
      "Review cancellation and refund policies",
    ],
    tips: [
      "Pre-paying can protect assets for Medicaid eligibility",
      "Some plans earn interest; others don't",
      "Consider whether the funeral home might go out of business",
      "Get everything in writing and keep copies safe",
    ],
  },

  // ============================================
  // LONG-TERM TASKS
  // ============================================

  "long-request-death-certificates": {
    title: "Requesting Death Certificates",
    description: "You'll need multiple certified copies for various legal and financial purposes.",
    steps: [
      "The funeral home typically orders the first batch",
      "Request 10-15 certified copies initially",
      "Additional copies can be ordered from the vital records office",
      "Keep track of which copies you've submitted where",
    ],
    tips: [
      "Certified copies have a raised seal or stamp",
      "Some institutions return certificates; others don't",
      "Keep at least a few copies for your permanent records",
      "Processing time varies by state",
    ],
  },

  "long-notify-social-security": {
    title: "Social Security Notification",
    description: "Social Security must be notified to stop benefits and to apply for survivor benefits if eligible.",
    steps: [
      "The funeral home usually reports the death to SSA",
      "Confirm this has been done within a week or two",
      "If not, call SSA at 1-800-772-1213",
      "Ask about survivor benefits for spouse or dependents",
    ],
    tips: [
      "Benefits received after death must be returned",
      "Surviving spouse may be eligible for survivor benefits",
      "A one-time death benefit of $255 may be available",
      "SSA doesn't notify other agencies - you must do so separately",
    ],
  },

  "long-notify-banks": {
    title: "Notifying Financial Institutions",
    description: "Banks and lenders need to know about the death to protect the estate and transition accounts.",
    steps: [
      "Gather account information and death certificates",
      "Contact each bank, credit card company, and lender",
      "Ask about joint account procedures and estate requirements",
      "Inquire about any credit life insurance on loans",
    ],
    tips: [
      "Joint accounts typically transfer to the surviving owner",
      "Individual accounts may be frozen pending probate",
      "Ask about removing the deceased from joint credit cards",
      "Check for automatic payments that need to be redirected",
    ],
  },

  "long-close-accounts": {
    title: "Closing Accounts and Subscriptions",
    description: "Closing unused accounts prevents fraud and stops unnecessary charges.",
    steps: [
      "Make a list of all accounts: utilities, subscriptions, memberships",
      "Contact each one with the account number and death certificate",
      "Request final bills and refunds for prepaid services",
      "Cancel automatic payments",
    ],
    tips: [
      "Check credit card and bank statements for recurring charges",
      "Don't forget digital subscriptions: streaming, apps, domains",
      "Some services have specific bereavement policies",
      "Keep the email account active temporarily to catch forgotten accounts",
    ],
  },

  "long-close-social-media": {
    title: "Managing Social Media Accounts",
    description:
      "Social media accounts can be memorialized, deleted, or managed based on the person's wishes or family preference.",
    steps: [
      "Check if the person left instructions for digital accounts",
      "Review each platform's policies on death (Facebook, Instagram, etc.)",
      "Decide: memorialize, delete, or request data download",
      "Submit necessary documentation to each platform",
    ],
    tips: [
      "Facebook allows memorialization with a legacy contact",
      "Some platforms delete accounts after inactivity",
      "Consider downloading photos and posts before deletion",
      "Remove the person from shared accounts you manage",
    ],
  },

  "long-settle-estate": {
    title: "Settling the Estate",
    description: "Estate settlement involves gathering assets, paying debts, and distributing what remains to heirs.",
    steps: [
      "Identify the executor or administrator",
      "Inventory all assets: accounts, property, valuables",
      "Notify creditors and pay valid debts",
      "Distribute remaining assets according to the will or state law",
    ],
    tips: [
      "Consider consulting an estate attorney, especially for complex estates",
      "Keep detailed records of all transactions",
      "Don't distribute assets until debts are settled",
      "Small estates may qualify for simplified procedures",
    ],
  },

  "long-probate-process": {
    title: "The Probate Process",
    description:
      "Probate is the legal process of validating a will and administering the estate under court supervision.",
    steps: [
      "File the will with the probate court",
      "Petition to be appointed executor/administrator",
      "Notify heirs, beneficiaries, and creditors",
      "File required inventories and accountings with the court",
    ],
    tips: [
      "Not all estates require probate - it depends on size and how assets are titled",
      "Probate can take months to years depending on complexity",
      "An attorney can guide you through jurisdiction-specific requirements",
      "Executor responsibilities include fiduciary duties to the estate",
    ],
  },

  "long-manage-property": {
    title: "Managing Property",
    description: "Decisions about real estate can wait, but the property needs care in the meantime.",
    steps: [
      "Ensure mortgage payments continue if applicable",
      "Keep utilities on and secure the property",
      "Decide on timeline for selling, renting, or transferring",
      "Get appraisals if needed for estate purposes",
    ],
    tips: [
      "Don't rush to sell - grief can affect judgment",
      "Property may transfer by deed, trust, or probate",
      "Consider hiring a property manager for distant properties",
      "Check if there's a reverse mortgage or other lien",
    ],
  },

  "long-property-insurance": {
    title: "Reviewing Property Insurance",
    description: "Insurance policies may need updating when the property owner dies.",
    steps: [
      "Notify insurance companies of the death",
      "Ensure coverage continues during estate settlement",
      "Update named insured when property transfers",
      "Review coverage amounts - they may need adjustment",
    ],
    tips: [
      "Vacant homes may have different coverage requirements",
      "Ask about estate or trust as named insured temporarily",
      "Don't let coverage lapse - mortgage companies may force-place expensive insurance",
      "Liability coverage is especially important for vacant properties",
    ],
  },

  "long-property-maintenance": {
    title: "Property Maintenance",
    description: "Keeping up the property prevents deterioration and maintains value.",
    steps: [
      "Arrange regular lawn care and snow removal",
      "Check the property periodically for issues",
      "Keep utilities on to prevent pipe freezing, etc.",
      "Address any maintenance issues promptly",
    ],
    tips: [
      "Ask a neighbor to keep an eye on the property",
      "Forward mail to prevent pile-up",
      "Consider a house sitter for extended periods",
      "Set lights on timers for security",
    ],
  },

  "long-permanent-pet-arrangements": {
    title: "Permanent Pet Arrangements",
    description: "Finding the right long-term home for pets ensures they're cared for.",
    steps: [
      "Check if the person left wishes for pet care",
      "Ask family members and close friends if they can adopt",
      "Contact breed-specific rescues if needed",
      "Ensure medical records and ownership transfer properly",
    ],
    tips: [
      "Pets grieve too - try to minimize disruption",
      "Include pet supplies and favorite items with the pet",
      "Some organizations specifically help pets of the deceased",
      "Consider the pet's age, temperament, and needs when placing",
    ],
  },

  "long-apply-va-benefits": {
    title: "VA Survivor Benefits",
    description: "Surviving spouses and dependents may be eligible for various VA benefits.",
    steps: [
      "Contact the VA at 1-800-827-1000 or visit va.gov",
      "Gather required documents: DD-214, marriage certificate, death certificate",
      "Apply for burial allowance (partial reimbursement)",
      "Apply for DIC (Dependency and Indemnity Compensation) if eligible",
    ],
    tips: [
      "Benefits vary based on service, disability status, and cause of death",
      "A Veterans Service Organization can help navigate applications",
      "There's no time limit for some benefits, but apply promptly for others",
      "Education benefits may be available for dependents",
    ],
  },

  "long-order-headstone-marker": {
    title: "Ordering Headstone or Marker",
    description: "A headstone or marker provides a permanent memorial at the gravesite.",
    steps: [
      "Veterans get a free headstone/marker from the VA",
      "For non-veterans, work with cemetery or monument company",
      "Choose material, size, and inscription",
      "Installation typically happens several months after burial",
    ],
    tips: [
      "Cemeteries often have restrictions on size and style",
      "VA headstones can be ordered even if buried in private cemetery",
      "Take time choosing the inscription - it's permanent",
      "Some families wait a year for the ground to settle before installing",
    ],
  },

  "long-update-beneficiaries": {
    title: "Updating Your Own Beneficiaries",
    description: "When a primary beneficiary dies, you should update your own accounts to reflect current wishes.",
    steps: [
      "Review beneficiary designations on all accounts",
      "Update life insurance, retirement accounts, and bank accounts",
      "Review and possibly update your own will",
      "Consider new contingent beneficiaries",
    ],
    tips: [
      "Beneficiary designations override wills",
      "This is a good time to review your own estate plan",
      "Consider per stirpes vs. per capita designations",
      "Keep a list of accounts with beneficiary designations",
    ],
  },

  "long-return-medical-equipment": {
    title: "Returning Medical Equipment",
    description: "Rented or loaned medical equipment should be returned to the supplier.",
    steps: [
      "Identify what was rented vs. owned",
      "Contact the equipment supplier to arrange pickup",
      "Hospice equipment is usually picked up automatically",
      "Donated equipment may be accepted by charities",
    ],
    tips: [
      "Check if any equipment was purchased and could be resold",
      "Some organizations accept donated mobility equipment",
      "Unused supplies (gloves, pads) can sometimes be donated",
      "Prescription medications should be disposed of properly",
    ],
  },

  "long-thank-you-notes": {
    title: "Sending Thank-You Notes",
    description: "Acknowledging those who provided support is a meaningful way to close this chapter.",
    steps: [
      "Make a list of people to thank: caregivers, visitors, food providers, donors",
      "Choose cards or write personal notes",
      "Express specific gratitude when possible",
      "Send within a few weeks to a couple months",
    ],
    tips: [
      "Preprinted acknowledgment cards are perfectly acceptable",
      "It's okay to ask family members to help write notes",
      "Don't let perfect be the enemy of good - brief is fine",
      "Thank the funeral home staff and officiant too",
    ],
  },

  "long-schedule-financial-review": {
    title: "Financial Planning Review",
    description: "A death often changes your financial picture. Reviewing your situation helps you plan ahead.",
    steps: [
      "Assess how income and expenses have changed",
      "Review your insurance coverage and needs",
      "Update your budget for the new normal",
      "Consider meeting with a financial planner",
    ],
    tips: [
      "Wait a few months before making major financial decisions",
      "Don't immediately change your will under emotional pressure",
      "Consider tax implications of inherited assets",
      "Make sure you understand any new income sources (pension, survivor benefits)",
    ],
  },

  "long-memorial-plans": {
    title: "Long-Term Memorials",
    description: "Creating lasting tributes keeps your loved one's memory alive and honors their legacy.",
    steps: [
      "Consider what causes or institutions the person cared about",
      "Explore options: scholarship, donation, naming opportunity, memorial event",
      "Involve family in deciding how to memorialize",
      "Plan how to sustain the memorial over time",
    ],
    tips: [
      "Memorials don't have to be expensive - they should be meaningful",
      "Annual events on birthdays or anniversaries can be powerful",
      "Planting a tree or garden creates a living memorial",
      "Some families create foundations or donor-advised funds",
    ],
  },

  "long-create-memory-book": {
    title: "Creating a Memory Book",
    description: "A memory book preserves stories, photos, and mementos for future generations.",
    steps: [
      "Gather photos, letters, and memorabilia",
      "Ask family members to write memories or stories",
      "Organize chronologically or by theme",
      "Create physical albums and/or digital versions",
    ],
    tips: [
      "Include stories, not just photos",
      "Record older relatives' memories before they're forgotten",
      "Online services make professional-looking books easy",
      "Make copies for multiple family members",
    ],
  },

  "long-heirlooms-belongings": {
    title: "Distributing Belongings",
    description: "Sorting through a loved one's possessions is emotional but necessary work.",
    steps: [
      "Don't rush - it's okay to wait months or longer",
      "Check the will for specific bequests",
      "Give family members the chance to request meaningful items",
      "Donate, sell, or dispose of remaining items thoughtfully",
    ],
    tips: [
      "Take photos of items before distributing",
      "Consider keeping a few unexpected items - you may regret letting go",
      "Estate sales can handle large quantities",
      "Some organizations pick up donations for free",
    ],
  },

  "long-cremains-final-disposition": {
    title: "Final Disposition of Cremains",
    description: "There's no rush to decide what to do with ashes. Take time to consider options.",
    steps: [
      "Research options: scattering, burial, keeping, dividing",
      "Check local laws about scattering",
      "Consider locations meaningful to the person",
      "Plan a ceremony if desired",
    ],
    tips: [
      "You can divide cremains for multiple purposes",
      "Some families scatter over time in different locations",
      "Cremains can be incorporated into memorial objects (glass, jewelry)",
      "Burial in a cemetery provides a permanent visiting place",
    ],
  },

  "preplanning-inform-family": {
    title: "Informing Family of Plans",
    description: "Ensuring family knows about pre-arrangements prevents confusion later.",
    steps: [
      "Tell close family members that arrangements have been made",
      "Share the name of the funeral home and contract location",
      "Provide copies of documents to trusted individuals",
      "Discuss any specific wishes for the service",
    ],
    tips: [
      "Store documents where family can easily find them",
      "Consider listing this information with your other important documents",
      "Review and remind family periodically as years pass",
      "Make sure at least two people know the details",
    ],
  },

  "preplanning-review-annually": {
    title: "Annual Review of Arrangements",
    description: "Wishes and circumstances change over time. Regular review keeps plans current.",
    steps: [
      "Review written wishes and pre-arrangements annually",
      "Update any changes in preferences",
      "Confirm the funeral home is still operating",
      "Update family members on any changes",
    ],
    tips: [
      "Set a recurring calendar reminder",
      "Review after major life events (moves, health changes)",
      "Check that contact information is current",
      "Make sure beneficiaries still align with your wishes",
    ],
  },

  "preplanning-create-directive": {
    title: "Creating Advance Directives",
    description: "Advance directives document healthcare wishes in case you can't speak for yourself.",
    steps: [
      "Create a living will stating treatment preferences",
      "Designate a healthcare proxy to make decisions",
      "Consider a POLST form for serious illness (physician orders)",
      "Share copies with doctors, family, and your proxy",
    ],
    tips: [
      "State-specific forms are often available free online",
      "Discuss your wishes with your healthcare proxy",
      "Keep original accessible - not in a safe deposit box",
      "Review and update after major health changes",
    ],
  },
};
